import { type ReactNode } from 'react';
import { Center, Grid, GridItem, Spinner, Text } from '@chakra-ui/react';

import { StandingsChartPanelSplit } from '@/components/standingsChart/StandingsChartPanel';
import type League from '@/models/league';
import type { leagueMember } from '@/models/user';
import { pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from '@/views/ProfilePage/Card';
import { InviteCard } from './InviteCard';
import { LeaderboardCard } from './LeaderboardCard';
import { useLeagueStandingsChartData } from './useLeagueStandingsChartData';

const desktopCardBase = {
  borderRadius: 0,
  w: 'full' as const,
  display: 'flex' as const,
  flexDirection: 'column' as const,
};

/** Stretches to fill remaining column space (leaderboard upper-left, matchday lower-right). */
const desktopCardFill = {
  ...desktopCardBase,
  flex: 1,
  minH: 0,
  h: 'full' as const,
};

/** Natural content height (points over time, invite). Overrides Profile Card lg height: full. */
const desktopCardAuto = {
  ...desktopCardBase,
  flexShrink: 0,
  h: 'auto' as const,
  height: 'unset' as const,
};

function LeagueDashboardShell(props: {
  leaderboard: ReactNode;
  invite: ReactNode;
  chartPanel: ReactNode;
  matchdayPanel: ReactNode;
  showChartTitle?: boolean;
}) {
  const { leaderboard, invite, chartPanel, matchdayPanel, showChartTitle = true } = props;

  return (
    <Grid templateColumns='1fr 1fr' gap={4} w='full' alignItems='stretch' id='standings-chart'>
      <GridItem minW={0} display='flex' flexDirection='column' gap={4} minH={0}>
        <Card {...desktopCardFill}>{leaderboard}</Card>
        <Card {...desktopCardAuto}>{invite}</Card>
      </GridItem>

      <GridItem minW={0} display='flex' flexDirection='column' gap={4} minH={0}>
        <Card {...desktopCardAuto}>
          {showChartTitle ? (
            <Text {...pageSectionTitleProps} mb={2} flexShrink={0}>
              Points over time
            </Text>
          ) : null}
          {chartPanel}
        </Card>
        <Card {...desktopCardFill}>{matchdayPanel}</Card>
      </GridItem>
    </Grid>
  );
}

export function LeagueDesktopDashboard(props: {
  league: League & { id: string };
  isAdmin: boolean;
  isGlobalLeague: boolean;
  tournamentStarted: boolean;
  copyStandingsAsync: (getText: () => Promise<string>) => Promise<void>;
  onViewMemberList: (uid: string, member: leagueMember) => void;
  inviteId: string;
  copyString: (s: string) => Promise<void>;
}) {
  const chartData = useLeagueStandingsChartData(props.league);

  const leaderboard = (
    <LeaderboardCard
      league={props.league}
      isAdmin={props.isAdmin}
      isGlobalLeague={props.isGlobalLeague}
      tournamentStarted={props.tournamentStarted}
      copyStandingsAsync={props.copyStandingsAsync}
      onViewMemberList={props.onViewMemberList}
      embedded
    />
  );

  const invite = (
    <InviteCard
      id={props.inviteId}
      isAdmin={props.isAdmin}
      copyString={props.copyString}
      league={props.league}
      embedded
    />
  );

  if (chartData.loading) {
    return (
      <LeagueDashboardShell
        leaderboard={leaderboard}
        invite={invite}
        chartPanel={
          <Center py={8}>
            <Spinner size='sm' />
          </Center>
        }
        matchdayPanel={null}
      />
    );
  }

  if (chartData.error) {
    return (
      <LeagueDashboardShell
        leaderboard={leaderboard}
        invite={invite}
        chartPanel={
          <Text fontSize='sm' color='gray.500'>
            Could not load standings history.
          </Text>
        }
        matchdayPanel={null}
      />
    );
  }

  if (!chartData.ready) {
    return (
      <LeagueDashboardShell
        leaderboard={leaderboard}
        invite={invite}
        chartPanel={null}
        matchdayPanel={null}
        showChartTitle={false}
      />
    );
  }

  const chartPanelData = {
    series: chartData.standings.series,
    maxMatchday: chartData.standings.maxMatchday,
    columns: chartData.columns,
    currentUid: chartData.currentUid,
    gameResults: chartData.gameResults,
  };

  return (
    <StandingsChartPanelSplit embedded {...chartPanelData}>
      {({ chartSection, matchdaySection }) => (
        <LeagueDashboardShell
          leaderboard={leaderboard}
          invite={invite}
          chartPanel={chartSection}
          matchdayPanel={matchdaySection}
        />
      )}
    </StandingsChartPanelSplit>
  );
}
